import type { ReactElement } from 'react';
import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ButtonGroup } from '@noah-labs/fe-shared-ui-components';
import { ChevronDownIcon } from 'lucide-react';
import Markdown from 'markdown-to-jsx';
import type { MarkdownRemarkFrontmatterFaqItems, Maybe } from '../../../generated/types';
import { MarkdownTypography } from '../../components/typography/MarkdownTypography';
import { TextWithNoahLogo } from '../../components/typography/TextWithNoahLogo';

type PpFaqSection = {
  faqItems: MarkdownRemarkFrontmatterFaqItems[];
  subtitle: Maybe<string> | undefined;
  textWithIconLabel: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const gradientBorderStyles = {
  after: {
    backgroundImage: 'linear-gradient(to left, #5229FF80, #FF660080)',
    bottom: '-1px',
    content: `''`,
    left: '-1px',
    position: 'absolute',
    right: '-1px',
    top: '-1px',
    zIndex: '-1',
  },
  base: {
    backgroundClip: 'padding-box',
    border: '1px solid transparent',
  },
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  padding: theme.spacing(2, 1),
  position: 'relative',
  ...gradientBorderStyles.base,
  borderRadius: theme.borderRadius.md,
  ':after': {
    borderRadius: theme.borderRadius.md,
    ...gradientBorderStyles.after,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  ...theme.typography.paragraphBodyM,
  a: {
    color: theme.palette.text.link,
  },
  color: theme.palette.text.dark,
  strong: {
    ...theme.typography.paragraphBodyMBold,
  },
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  justifyContent: 'flex-start',
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: theme.spacing(2),
  },
}));

const StyledButton = styled(Button)<{ active?: boolean }>(({ active, theme }) => ({
  background: active
    ? theme.palette.websiteBrand.greyscale.black
    : theme.palette.websiteBrand.greyscale.white,
  borderRadius: theme.borderRadius.sm,
  color: active
    ? theme.palette.websiteBrand.greyscale.white
    : theme.palette.websiteBrand.greyscale.black,
  padding: theme.spacing(1, 2),
  ':after': {
    borderRadius: theme.borderRadius.sm,
    ...gradientBorderStyles.after,
  },
  ':hover': {
    backgroundColor: theme.palette.websiteBrand.greyscale.black,
    color: theme.palette.websiteBrand.greyscale.white,
    textDecoration: 'none',
    transition: 'all 0.2s linear',
  },
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  maxWidth: theme.breakpoints.values.lg,
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 3),
  },
})) as typeof Container;

export function FaqSection({
  faqItems,
  subtitle,
  textWithIconLabel,
  title,
}: PpFaqSection): ReactElement {
  const categories = faqItems.map((item) => item.faqCategory);
  const [selectedCategory, setSelectedCategory] = useState(categories[0]);
  const [openAccordions, setOpenAccordions] = useState<number[]>([]);
  const faqItemsByCategory = faqItems.find((item) => item.faqCategory === selectedCategory);

  return (
    <StyledContainer disableGutters fixed component="section">
      <Stack spacing={5}>
        <Stack spacing={2.5}>
          <div>
            <TextWithNoahLogo>{textWithIconLabel}</TextWithNoahLogo>
            <Typography variant="headingL">{title}</Typography>
          </div>
          <MarkdownTypography>{subtitle}</MarkdownTypography>
        </Stack>
        <StyledButtonGroup direction="row" spacing={4}>
          {categories.map((category) => {
            if (!category) {
              return null;
            }
            return (
              <StyledButton
                key={category}
                active={category === selectedCategory}
                onClick={(): void => {
                  setOpenAccordions([]);
                  setSelectedCategory(category);
                }}
              >
                {category}
              </StyledButton>
            );
          })}
        </StyledButtonGroup>
        <Grid key={selectedCategory} container rowGap={1}>
          {faqItemsByCategory?.questions?.map((item, index) => {
            const isActive = openAccordions.includes(index);
            return (
              <Grid key={item?.id} size={{ md: 6, xs: 12 }}>
                <StyledAccordion
                  disableGutters
                  elevation={0}
                  expanded={isActive}
                  onChange={(_e, expanded): void => {
                    if (expanded && !isActive) {
                      setOpenAccordions((prevState) => [...prevState, index]);
                    } else if (!expanded && isActive) {
                      setOpenAccordions((prevState) =>
                        prevState.filter((stateItem) => stateItem !== index),
                      );
                    }
                  }}
                >
                  <AccordionSummary expandIcon={<ChevronDownIcon />}>
                    <Typography variant="paragraphBodyM">{item?.question}</Typography>
                  </AccordionSummary>
                  {item?.answer && (
                    <StyledAccordionDetails>
                      <Markdown>{item?.answer}</Markdown>
                    </StyledAccordionDetails>
                  )}
                </StyledAccordion>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </StyledContainer>
  );
}
